import type { ComponentType } from "react";

import * as ReactWidgets from "@fluid-app/react-widgets";

import type { PropInfo } from "./utils/createWebComponent";
import bannerWidgetProps, {
  propInfo as bannerPropInfo,
} from "../generated/banner-widget-props.js";
// Statically import all prop files to bundle them directly
import cartWidgetProps, {
  propInfo as cartPropInfo,
} from "../generated/cart-widget-props.js";
import leadCaptureWidgetProps, {
  propInfo as leadCapturePropInfo,
} from "../generated/lead-capture-widget-props.js";
import mediaWidgetProps, {
  propInfo as mediaPropInfo,
} from "../generated/media-widget-props.js";
// Import the generated manifest
// Remove unused imports and variables
// Remove: import widgetManifest from "../generated/widget-manifest.js";
// Remove: const fluidWindow = window as FluidWindow;
import { createWebComponent } from "./utils/createWebComponent";

// Define the structure of the manifest entries
interface WidgetInfo {
  dirName: string;
  componentName: string;
  tagName: string;
  propsJsPath: string;
}

// Create a static registry of prop information
const STATIC_PROP_REGISTRY = {
  "cart-widget": {
    propNames: cartWidgetProps,
    propInfo: cartPropInfo as PropInfo[],
  },
  "banner-widget": {
    propNames: bannerWidgetProps,
    propInfo: bannerPropInfo as PropInfo[],
  },
  "lead-capture-widget": {
    propNames: leadCaptureWidgetProps,
    propInfo: leadCapturePropInfo as PropInfo[],
  },
  "media-widget": {
    propNames: mediaWidgetProps,
    propInfo: mediaPropInfo as PropInfo[],
  },
} as const;

// Remove the FluidWindow interface definition since it is no longer used

export async function registerSingleWebComponent(
  widget: WidgetInfo,
  widgetProps:
    | ReactWidgets.CartWidgetProps
    | ReactWidgets.LeadCaptureWidgetProps
    | ReactWidgets.MediaWidgetProps,
): Promise<void> {
  if (typeof window === "undefined" || typeof customElements === "undefined") {
    console.warn(
      "Web components registration failed: not running in a browser environment",
    );
    return;
  }
  // Check if component is already registered - be more explicit about the check
  const existingElement = customElements.get(widget.tagName);
  if (existingElement) {
    console.log(`Custom element "${widget.tagName}" is already registered.`);
    return;
  }

  // Dynamically access the potential React component from the imported module
  // Cast ReactWidgets to an indexable type to allow string access
  const PotentialComponent = (ReactWidgets as Record<string, unknown>)[
    widget.componentName
  ];

  // Check if the accessed property is a function (React components are functions/classes)
  if (typeof PotentialComponent !== "function") {
    console.warn(
      `Could not find a valid React component "${widget.componentName}" in @fluid-app/react-widgets exports, or it was not a function/class.`,
    );
    return;
  }

  // Now we know PotentialComponent is a function, treat it as the component
  const Component = PotentialComponent as ComponentType<unknown>;

  // Load prop information
  const { propNames, propInfo } = await loadPropInfo(widget);

  // Create and define the web component
  // Use detailed prop info if available, otherwise fall back to prop names
  const propsForComponent = propInfo.length > 0 ? propInfo : propNames;
  const WebComponentElement = createWebComponent(
    Component,
    propsForComponent,
    widget.tagName,
    widgetProps,
  );

  // Final check before defining - double-check that the element isn't defined
  // This handles race conditions where another script might have defined it
  // between our initial check and component creation
  if (customElements.get(widget.tagName)) {
    console.log(
      `Custom element "${widget.tagName}" was defined during component creation. Skipping definition.`,
    );
    return;
  }

  try {
    // Try to define the custom element with comprehensive error handling
    customElements.define(widget.tagName, WebComponentElement);
    console.log(
      `✅ Successfully registered custom element "${widget.tagName}"`,
    );
    // After registration, check if an element with this tag already exists in the DOM
    if (!document.querySelector(widget.tagName)) {
      const el = document.createElement(widget.tagName);
      // for (const [key, value] of Object.entries(widgetProps)) {
      //   if (
      //     typeof value === "string" ||
      //     typeof value === "number" ||
      //     typeof value === "boolean"
      //   ) {
      //     el.setAttribute(key, String(value));
      //   } else {
      //     // For objects/functions, assign as property (type-safe)
      //     (el as unknown as Record<string, unknown>)[key] = value;
      //   }
      // }
      document.body.appendChild(el);
      // console.log(
      //   `Appended <${widget.tagName}> to document.body with props`,
      //   widgetProps,
      // );
    } else {
      console.log(
        `Element <${widget.tagName}> already exists in DOM, not appending.`,
      );
    }
  } catch (defineError) {
    // Handle the specific error we're trying to prevent
    if (
      defineError instanceof Error &&
      (defineError.message.includes("already been defined") ||
        defineError.message.includes("already been used with this registry") ||
        defineError.message.includes("has already been used"))
    ) {
      // This is the exact error from the issue - log it but don't fail
      console.log(
        `Custom element "${widget.tagName}" already defined elsewhere. Skipping registration.`,
      );
    } else {
      // For other errors, log warning but don't fail the entire registration process
      console.error(
        `Error defining custom element "${widget.tagName}":`,
        defineError,
      );
    }
  }
}

/**
 * Load prop information for a widget
 */
async function loadPropInfo(
  widget: WidgetInfo,
): Promise<{ propNames: string[]; propInfo: PropInfo[] }> {
  // First try to get props from static registry (bundled)
  if (widget.dirName in STATIC_PROP_REGISTRY) {
    const staticProps =
      STATIC_PROP_REGISTRY[widget.dirName as keyof typeof STATIC_PROP_REGISTRY];
    console.log(`✅ Loaded props for ${widget.tagName} from static registry`);
    return {
      propNames: staticProps.propNames,
      propInfo: staticProps.propInfo,
    };
  }

  // Fallback: try loading from /generated folder
  try {
    const propFileName = `${widget.dirName}-props.js`;
    const propsModule = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ `/generated/${propFileName}`
    )) as {
      default: string[];
      propInfo?: PropInfo[];
    };

    if (Array.isArray(propsModule.default)) {
      console.log(
        `✅ Loaded props for ${widget.tagName} from /generated/${propFileName}`,
      );
      return {
        propNames: propsModule.default,
        propInfo: propsModule.propInfo ?? [],
      };
    }
  } catch (error) {
    console.debug(`Failed to load props from /generated folder:`, error);
  }

  // If all else fails, return empty props
  console.warn(
    `⚠️  Could not load props for ${widget.tagName}. Widget will work but without prop validation.`,
  );
  return { propNames: [], propInfo: [] };
}

// Export types for documentation (This part becomes less useful as components are dynamic)
// Consider generating a separate types file or improving documentation.
// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now

// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now
